import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Mail, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shown once, the first time a user reaches the dashboard. After they make a
 * choice the `digest_welcome_shown` flag is set on the server so this never
 * fires again — they can change their mind from the dashboard toggle.
 */
export default function WelcomeDigestModal({ open, onResolved }) {
  const [saving, setSaving] = useState(false);

  const respond = async (digestEnabled) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/users/me/preferences`,
        { digest_enabled: digestEnabled, digest_welcome_shown: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(digestEnabled
        ? "You're signed up for the weekly digest — first one lands Monday morning."
        : "No problem — you can turn it on anytime from your dashboard.");
      onResolved?.(digestEnabled);
    } catch (err) {
      toast.error('Failed to save your preference — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* not dismissible without choosing */ }}>
      <DialogContent className="welcome-digest-dialog" data-testid="welcome-digest-modal">
        <DialogHeader>
          <DialogTitle>
            <Sparkles size={20} style={{ display: 'inline-block', marginRight: 8, verticalAlign: '-3px', color: 'var(--primary)' }} />
            Welcome to Rental P.A.!
          </DialogTitle>
          <DialogDescription>
            Get started by deciding whether you'd like a weekly email summary of upcoming renewals and expiring documents.
          </DialogDescription>
        </DialogHeader>
        <div className="welcome-digest-body">
          <div className="welcome-digest-icon">
            <Mail size={48} />
          </div>
          <p className="welcome-digest-question">
            Would you like to receive a weekly email summary of upcoming renewals and expiring documents?
          </p>
          <ul className="welcome-digest-features">
            <li>Tenancies ending in the next 30 days</li>
            <li>Compliance documents expiring soon</li>
            <li>Overdue items highlighted at the top</li>
          </ul>
          <p className="welcome-digest-fineprint">
            Sent every Monday at 08:00. You can change this anytime in settings.
          </p>
        </div>
        <div className="welcome-digest-actions">
          <Button
            type="button"
            onClick={() => respond(false)}
            className="btn-secondary"
            disabled={saving}
            data-testid="welcome-digest-decline"
          >
            No thanks, maybe later
          </Button>
          <Button
            type="button"
            onClick={() => respond(true)}
            className="btn-primary"
            disabled={saving}
            data-testid="welcome-digest-accept"
          >
            {saving ? 'Saving…' : 'Yes, sign me up'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
